import type { ApolloCache } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client';
import React, { useEffect, useRef, useState } from 'react';
import * as styles from './AdminAppearanceView.module.scss';
import CustomDomainSettings from './CustomDomainSettings';
import redirectToHost from './redirectToHost';
import splitSubdomain from './splitSubdomain';
import TeamIconEditor from './TeamIconEditor';
import useUpdateOrganization from './useUpdateOrganization';
import { SessionActions } from '../../account/sessionSlice';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Card, { Cards } from '../../components/Card';
import ConfirmationModal from '../../components/ConfirmationModal';
import Form, { Field, Submit } from '../../components/Form';
import { gql } from '../../graphql/generated';
import SettingCard from '../../settings/SettingCard';
import SettingsContainerView from '../../settings/SettingsContainerView';
import SettingsSection from '../../settings/SettingsSection';
import { UIActions } from '../../ui/uiSlice';
import handleApolloError from '../../utils/error/handleApolloError';
import getFeature from '../../utils/features';
import { updateQueryList } from '../../utils/graphql/updateQueryList';

const useDefaultHostRedirect = ({
  enabled,
  effectiveHost,
  host,
  customDomains,
}: {
  enabled: boolean;
  customDomains: { hostname: string }[];
  effectiveHost: string;
  host: string;
}) => {
  const dispatch = useAppDispatch();
  const prevEffectiveHostRef = useRef(effectiveHost);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    const effectiveHostChanged = effectiveHost !== prevEffectiveHostRef.current;
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    const currentHostRemoved = ![
      ...customDomains.map(({ hostname }) => hostname),
      host,
    ].includes(window.location.hostname);
    if (
      (effectiveHostChanged && window.location.hostname !== effectiveHost) ||
      currentHostRemoved
    ) {
      redirectToHost(effectiveHost, () => {
        if (currentHostRemoved) {
          dispatch(SessionActions.clearSession());
        }
      });
    }
  }, [enabled, dispatch, host, effectiveHost, customDomains]);

  useEffect(() => {
    prevEffectiveHostRef.current = effectiveHost;
  }, [enabled, effectiveHost]);
};

const CUSTOM_DOMAINS_QUERY = gql(/** @complexity 4 */ `
  query customDomainsSettings {
    session: currentSession {
      organization {
        id
        customDomains {
          ...CustomDomainSettings_customDomains
        }
      }
    }
  }
`);

const CREATE_CUSTOM_DOMAIN_MUTATION = gql(/** @complexity 2 */ `
  mutation createCustomDomain($hostname: String!) {
    customDomain: createCustomDomain(hostname: $hostname) {
      ...CustomDomainSettings_customDomains
    }
  }
`);

const DELETE_CUSTOM_DOMAIN_MUTATION = gql(/** @complexity 2 */ `
  mutation deleteCustomDomain($hostname: ID!) {
    customDomain: deleteCustomDomain(hostname: $hostname) {
      hostname
    }
  }
`);

const SET_DEFAULT_CUSTOM_DOMAIN_MUTATION = gql(/** @complexity 2 */ `
  mutation setDefaultCustomDomain($hostname: ID!) {
    organization: setDefaultCustomDomain(hostname: $hostname) {
      id
      effectiveHost
    }
  }
`);

const CLEAR_DEFAULT_CUSTOM_DOMAIN_MUTATION = gql(/** @complexity 2 */ `
  mutation clearDefaultCustomDomain {
    organization: clearDefaultCustomDomain {
      id
      effectiveHost
    }
  }
`);

interface AdminAppearanceViewContentProps {
  name: string;
  host: string;
  effectiveHost: string;
  disableDefaultHostRedirect: boolean;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const AdminAppearanceViewContent = ({
  name,
  host,
  effectiveHost,
  disableDefaultHostRedirect,
}: AdminAppearanceViewContentProps) => {
  const dispatch = useAppDispatch();
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingSubdomain, setIsEditingSubdomain] = useState(false);
  const updateOrganization = useUpdateOrganization({ host });
  const handleNameChange = (values: { name: string }) =>
    updateOrganization({ name: values.name }).then(() => {
      setIsEditingName(false);
    });

  const isCustomDomainEnabled = getFeature('custom_domain');

  const [subdomain, baseDomain] = splitSubdomain(host);

  const handleSubdomainChange = (values: { subdomain?: string }) => {
    if (values.subdomain === subdomain) {
      setIsEditingSubdomain(false);
      return;
    }

    let isResolved = false;
    dispatch(
      UIActions.openModal(ConfirmationModal, {
        title: 'Change default domain?',
        message: `Any links to your previous domain ${host} will no longer work.`,
        confirmButtonText: 'Change',
        close: () => {
          dispatch(UIActions.closeModal());
          if (!isResolved) {
            setIsEditingSubdomain(false);
          }
        },
        onConfirm: async () => {
          isResolved = true;
          try {
            await updateOrganization({ subdomain: values.subdomain });
            setIsEditingSubdomain(false);
          } catch (error) {
            handleApolloError(error, {
              VALIDATION: (graphqlError: { message?: string }) => {
                const message = graphqlError?.message?.replace(
                  /^VALIDATION: \[subdomain\]/,
                  'Subdomain',
                );
                if (message) {
                  dispatch(
                    UIActions.pushToast({ level: 'error', message }, 3000),
                  );
                }
              },
            });
          }
        },
      }),
    );
  };

  const customDomainsResponse = useQuery(CUSTOM_DOMAINS_QUERY, {
    fetchPolicy: 'network-only',
  });

  const [createCustomDomain] = useMutation(CREATE_CUSTOM_DOMAIN_MUTATION, {
    update: updateQueryList(
      { query: CUSTOM_DOMAINS_QUERY },
      'session.organization.customDomains',
      'customDomain',
      true,
    ),
  });

  const [deleteCustomDomain] = useMutation(DELETE_CUSTOM_DOMAIN_MUTATION, {
    update: updateQueryList(
      { query: CUSTOM_DOMAINS_QUERY },
      'session.organization.customDomains',
      'customDomain',
      false,
    ),
  });

  const updateOrganizationCache = (
    cache: ApolloCache<unknown>,
    response?: {
      data?: {
        organization?: {
          id: string;
        } | null;
      } | null;
    } | null,
  ) => {
    try {
      const previousData = cache.readQuery({ query: CUSTOM_DOMAINS_QUERY });
      const prevOrg = previousData?.session?.organization;
      const nextOrg = response?.data?.organization;
      if (prevOrg && nextOrg && prevOrg.id === nextOrg.id) {
        cache.writeQuery({
          query: CUSTOM_DOMAINS_QUERY,
          data: {
            session: {
              ...previousData.session,
              organization: { ...prevOrg, ...nextOrg },
            },
          },
        });
      }
    } catch (e) {
      // readQuery failed
    }
  };

  const [setDefaultCustomDomain] = useMutation(
    SET_DEFAULT_CUSTOM_DOMAIN_MUTATION,
    {
      update: updateOrganizationCache,
    },
  );

  const [clearDefaultCustomDomain] = useMutation(
    CLEAR_DEFAULT_CUSTOM_DOMAIN_MUTATION,
    {
      update: updateOrganizationCache,
    },
  );

  const changeDefaultCustomDomain = (hostname: string, isDefault: boolean) => {
    if (isDefault) {
      setDefaultCustomDomain({ variables: { hostname } }).then(
        (res) =>
          res?.data &&
          dispatch(
            SessionActions.setCurrentOrganization(res.data.organization),
          ),
      );
    } else {
      clearDefaultCustomDomain().then(
        (res) =>
          res.data &&
          dispatch(
            SessionActions.setCurrentOrganization(res.data.organization),
          ),
      );
    }
  };

  const customDomains =
    customDomainsResponse.data?.session?.organization?.customDomains ?? [];

  useDefaultHostRedirect({
    enabled: !customDomainsResponse.loading && !disableDefaultHostRedirect,
    customDomains,
    host,
    effectiveHost,
  });

  return (
    <>
      <SettingsSection>
        <Cards>
          <Card>
            <TeamIconEditor />
          </Card>
          {isEditingName ? (
            <SettingCard
              description={
                <Form<{ name: string }>
                  initialValues={{ name }}
                  onSubmit={handleNameChange}
                >
                  <div className={styles.fieldContainer}>
                    <Field
                      className={styles.field}
                      id="organizationName"
                      margin="none"
                      name="name"
                      required
                    />
                    <div>
                      <Submit variant="primary">Save</Submit>
                    </div>
                  </div>
                </Form>
              }
              title={<label htmlFor="organizationName">Team name</label>}
            />
          ) : (
            <SettingCard
              actions={
                <Button
                  onClick={() => setIsEditingName(true)}
                  variant="secondary"
                >
                  Edit
                </Button>
              }
              title="Team name"
              description={name}
            />
          )}
          {isEditingSubdomain ? (
            <SettingCard
              description={
                <Form<{ subdomain?: string }>
                  initialValues={{ subdomain }}
                  onSubmit={handleSubdomainChange}
                >
                  <div className={styles.fieldContainer}>
                    <Field
                      append={baseDomain}
                      className={styles.field}
                      id="subdomain"
                      margin="none"
                      name="subdomain"
                      required
                    />
                    <div>
                      <Submit variant="primary">Save</Submit>
                    </div>
                  </div>
                </Form>
              }
              title={<label htmlFor="subdomain">Subdomain</label>}
            />
          ) : (
            <SettingCard
              actions={
                <Button
                  onClick={() => setIsEditingSubdomain(true)}
                  variant="secondary"
                >
                  Edit
                </Button>
              }
              title="Subdomain"
              description={
                <>
                  {host}
                  {isCustomDomainEnabled &&
                    customDomains.length > 0 &&
                    host === effectiveHost && (
                      <span className={styles.badgeContainer}>
                        <Badge color="yellow">Default</Badge>
                      </span>
                    )}
                </>
              }
            />
          )}
        </Cards>
      </SettingsSection>
      <CustomDomainSettings
        customDomains={customDomains}
        loading={customDomainsResponse.loading}
        host={host}
        effectiveHost={effectiveHost}
        createCustomDomain={async (hostname) =>
          createCustomDomain({ variables: { hostname } })
        }
        deleteCustomDomain={async (hostname) =>
          deleteCustomDomain({ variables: { hostname } })
        }
        changeDefaultCustomDomain={changeDefaultCustomDomain}
      />
    </>
  );
};

const AdminAppearanceView = () => {
  const name = useAppSelector((state) => state.session.organization.name);
  const host = useAppSelector((state) => state.session.organization.host);
  const effectiveHost = useAppSelector(
    (state) => state.session.organization.effectiveHost,
  );

  return (
    <SettingsContainerView title="Appearance">
      <AdminAppearanceViewContent
        name={name}
        host={host}
        effectiveHost={effectiveHost}
        disableDefaultHostRedirect={false}
      />
    </SettingsContainerView>
  );
};

export default AdminAppearanceView;
